import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Table} from '@/components/commons/table/Table'
import {Button} from '@/components/ui/button/Button'
import {FiltersBar} from '@/components/ui/filters-bar/FiltersBar'
import {AlertCircleIcon, AlertTriangleIcon, Download01Icon, SquareIcon} from '@/components/ui/icon/Icon'
import i18n, {useTranslation} from '@/translations/i18n'
import {z} from 'zod'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useURLParsedParams} from '@/hooks/useURLParsedParams'
import {generatePath, Link, useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {UserDetailsUrlParams} from '@/types'
import {UserEventsColumn} from '@/features/users/types'
import {useGetUserEvents} from '@/features/users/queries/useGetUserEvents'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload
} from '@/utilities/helpers'
import {TableBodyCell, TableBodyCellText, TableBodyRow} from '@/components/ui/table-atoms/TableAtoms'
import {SelectRowCheckbox} from '@/components/ui/select-row-checkbox/SelectRowCheckbox'
import {SelectColumnHeader} from '@/components/ui/select-column-header/SelectColumnHeader'
import {usePermissions} from '@/features/auth/store/store'
import {StyledSelectCell, StyledUserDetailsEventsWrapper} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {SelectedRowsText} from '@/components/commons/selected-rows-text/SelectedRowsText'
import {httpExportUserEvents} from '@/features/users/http/users.http'
import {TableFilters} from '@/components/commons/table-filters/TableFilters'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {ColumnVisibilityMenu} from '@/components/commons/columns-visibility-menu/ColumnVisibilityMenu'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {agendaEventStatusMapToColor, agendaEventStatusMapToLabel, eventTypeMapToLabel} from '@/features/agenda/utils'
import {projectSectorToLabel} from '@/features/projects/utils'
import {routes} from '@/utilities/constants/routes'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useTheme} from 'styled-components'
import {EventSmall} from '@/features/agenda/types'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'
import {userEventColumnToLabel as columnNameToLabel} from '@/features/users/utils'

const makeColumnsMap = () =>
    [
        {
            id: UserEventsColumn.enum.date,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.date])
        },
        {
            id: UserEventsColumn.enum.time,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.time])
        },
        {
            id: UserEventsColumn.enum.status,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.status])
        },
        {
            id: UserEventsColumn.enum.accessType,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.accessType])
        },
        {
            id: UserEventsColumn.enum.patientId,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.patientId])
        },
        {
            id: UserEventsColumn.enum.sector,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.sector])
        },
        {
            id: UserEventsColumn.enum.isRemote,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.isRemote])
        },
        {
            id: UserEventsColumn.enum.projectCode,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.projectCode])
        },
        {
            id: UserEventsColumn.enum.projectStartDate,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.projectStartDate])
        },
        {
            id: UserEventsColumn.enum.projectExpirationDate,
            label: i18n.t(columnNameToLabel[UserEventsColumn.enum.projectExpirationDate])
        }
    ] as const satisfies {id: UserEventsColumn; label: string}[]

/* The events are already scoped on the consultant, so the project shown per row is the one of the
event's patient. Group treatments carry more than one patient: the first one wins, consistently with
the patient visits table. */
const getEventProject = (event: EventSmall): NonNullable<EventSmall['eventPatients']>[number]['project'] | undefined =>
    event.eventPatients?.[0]?.project ?? undefined

export const UserDetailsEvents = () => {
    const {t} = useTranslation()
    const {spacing} = useTheme()
    const permissions = usePermissions()
    const columnsMap = makeColumnsMap()
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedEventsIds, setUnselectedEventsIds] = useState<string[]>([])
    const [selectedEventsIds, setSelectedEventsIds] = useState<string[]>([])

    const urlParams = useURLParsedParams(UserDetailsUrlParams)
    const userId = urlParams.success ? urlParams.data.id : undefined
    const {searchParams, setSearchParams} = useParsedSearchParams(
        z.object({
            limit: z.coerce.number().int().positive().default(50),
            page: z.coerce.number().int().positive().default(1),
            filters: z.string().optional(),
            sorts: z.string().optional(),
            columnVisibility: z.string().optional()
        })
    )
    const visitsQuery = useGetUserEvents(
        {userId: userId ?? ''},
        {
            limit: searchParams.limit,
            page: searchParams.page
        },
        {
            filters: searchParams.filters ? JSON.parse(searchParams.filters) : undefined,
            sorts: searchParams.sorts ? JSON.parse(searchParams.sorts) : undefined
        },
        {enabled: !!userId}
    )

    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedEventsIds([])
        setUnselectedEventsIds([])
    }

    const selectedUserEventsCount = isSelectAll
        ? visitsQuery.persistedPagination.total - unselectedEventsIds.length
        : selectedEventsIds.length

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(UserEventsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(UserEventsColumn.options.map(column => [column, true]))

    const navigate = useNavigate()

    if (!userId) {
        return <ErrorBox icon={<AlertCircleIcon />} title={t('errors:invalid_link')} />
    }

    return (
        <StyledUserDetailsEventsWrapper>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    {selectedUserEventsCount >= 1 && (
                        <>
                            <SelectedRowsText count={selectedUserEventsCount} />
                            <ExportModal
                                trigger={
                                    <Button variant="ghost" size="sm">
                                        <Download01Icon />
                                        {t('commons:export')}
                                    </Button>
                                }
                                asyncExport
                                fileName="Visite consulente"
                                promise={({extension, mode}) =>
                                    httpExportUserEvents({
                                        urlParams: {userId},
                                        payload: {
                                            selectedVisitIds: selectedEventsIds,
                                            exceptedVisitIds: unselectedEventsIds,
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'view'
                                                    ? UserEventsColumn.exclude(['select']).options.filter(
                                                          column => columnVisibility[column]
                                                      )
                                                    : UserEventsColumn.exclude(['select']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }
                                selectedCount={selectedUserEventsCount}
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                            />
                        </>
                    )}
                </Flexbox>
                {selectedUserEventsCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="userEvents"
                            availableFilters={[
                                {
                                    key: UserEventsColumn.enum.date,
                                    label: columnNameToLabel[UserEventsColumn.enum.date]
                                },
                                {
                                    key: UserEventsColumn.enum.time,
                                    label: columnNameToLabel[UserEventsColumn.enum.time]
                                },
                                {
                                    key: UserEventsColumn.enum.status,
                                    label: columnNameToLabel[UserEventsColumn.enum.status]
                                },
                                {
                                    key: UserEventsColumn.enum.accessType,
                                    label: columnNameToLabel[UserEventsColumn.enum.accessType]
                                },
                                {
                                    key: UserEventsColumn.enum.patientId,
                                    label: columnNameToLabel[UserEventsColumn.enum.patientId]
                                },
                                {
                                    key: UserEventsColumn.enum.isRemote,
                                    label: columnNameToLabel[UserEventsColumn.enum.isRemote]
                                },
                                {
                                    key: UserEventsColumn.enum.projectCode,
                                    label: columnNameToLabel[UserEventsColumn.enum.projectCode]
                                },
                                {
                                    key: UserEventsColumn.enum.projectStartDate,
                                    label: columnNameToLabel[UserEventsColumn.enum.projectStartDate]
                                },
                                {
                                    key: UserEventsColumn.enum.projectExpirationDate,
                                    label: columnNameToLabel[UserEventsColumn.enum.projectExpirationDate]
                                }
                            ]}
                        />
                        <SortingMenu
                            /* The patient column sorts by `patientFullName` (last name, first name):
                               `patientId` is not a sortable key on the backend. */
                            columns={columnsMap.map(column =>
                                column.id == UserEventsColumn.enum.patientId
                                    ? {...column, id: 'patientFullName'}
                                    : column
                            )}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={columnVisibility}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            {visitsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('patients:no_visits_title')}
                            subtitle={searchParams.filters ? t('patients:no_visits_description') : undefined}
                        >
                            {searchParams.filters && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchParams({filters: undefined, page: 1})
                                    }}
                                >
                                    {t('patients:no_visits_cta')}
                                </Button>
                            )}
                        </EmptyState>
                    }
                    pagination={{
                        ...visitsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [UserEventsColumn.enum.select],
                        right: []
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedEventsIds,
                            unselectedIds: unselectedEventsIds,
                            isSelectAll
                        })
                    }
                    isLoading={visitsQuery.isPending}
                    data={visitsQuery.data?.data ?? []}
                    rowComponent={({tableBodyRowProps, index, dataItem}) => (
                        <TableBodyRow
                            key={index}
                            onClick={() =>
                                dataItem
                                    ? navigate({
                                          pathname: generatePath(routes.AGENDA.path, {
                                              entity: 'users',
                                              view: 'day',
                                              date: dayjs(dataItem?.date).format('DD-MM-YYYY')
                                          }),
                                          search: new URLSearchParams({
                                              category: 'custom',
                                              subjectIds: userId
                                          }).toString()
                                      })
                                    : undefined
                            }
                            {...tableBodyRowProps}
                        />
                    )}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledSelectCell>
                                        {permissions?.users.write && (
                                            <SelectRowCheckbox
                                                isSelectAll={isSelectAll}
                                                setSelectedIds={setSelectedEventsIds}
                                                getIsRowSelected={({dataItem}) =>
                                                    getIsRowSelected({
                                                        dataItem,
                                                        selectedIds: selectedEventsIds,
                                                        unselectedIds: unselectedEventsIds,
                                                        isSelectAll
                                                    })
                                                }
                                                dataItem={dataItem}
                                                setUnselectedIds={setUnselectedEventsIds}
                                            />
                                        )}
                                    </StyledSelectCell>
                                </TableBodyCell>
                            ),
                            header: () =>
                                permissions?.users.write ? (
                                    <SelectColumnHeader
                                        currentPageIds={visitsQuery.data?.data.map(visit => visit.id) ?? []}
                                        isSelectAll={isSelectAll}
                                        selectedIds={selectedEventsIds}
                                        setIsSelectAll={setIsSelectAll}
                                        unselectedIds={unselectedEventsIds}
                                        setUnselectedIds={setUnselectedEventsIds}
                                        setSelectedIds={setSelectedEventsIds}
                                        selectedCount={selectedUserEventsCount}
                                        paginationTotal={visitsQuery.persistedPagination.total}
                                    />
                                ) : (
                                    <></>
                                ),
                            id: UserEventsColumn.enum.select,
                            size: () => 64
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.date, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.date]),
                            id: UserEventsColumn.enum.date,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.time
                                            ? formatLocaleDate(
                                                  `${formatLocaleDate(dataItem.date, 'YYYY-MM-DD')} ${dataItem.time}`,
                                                  'HH:mm'
                                              )
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.time]),
                            id: UserEventsColumn.enum.time,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.status ? (
                                        <Flexbox align="center" gap={2}>
                                            <SquareIcon
                                                className="custom"
                                                size={8}
                                                color={agendaEventStatusMapToColor[dataItem.status]}
                                            />
                                            <TableBodyCellText>
                                                {t(agendaEventStatusMapToLabel[dataItem.status])}
                                            </TableBodyCellText>
                                        </Flexbox>
                                    ) : (
                                        '-'
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.status]),
                            id: UserEventsColumn.enum.status,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText weight="bold">
                                        {dataItem.eventConsultants?.[0]?.accessType?.accessType
                                            ? t(eventTypeMapToLabel[dataItem.eventConsultants[0].accessType.accessType])
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.accessType]),
                            id: UserEventsColumn.enum.accessType,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const patients = dataItem.eventPatients ?? []

                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        {patients.length === 0 ? (
                                            <TableBodyCellText variant="normal">{t('commons:none')}</TableBodyCellText>
                                        ) : (
                                            <Flexbox align="center" gap={1.5} fullWidth>
                                                {patients.slice(0, 2).map((eventPatient, index) => (
                                                    <Avatar
                                                        key={eventPatient.patientId}
                                                        style={
                                                            index > 0
                                                                ? {marginLeft: -(spacing * 4)}
                                                                : {position: 'relative'}
                                                        }
                                                        size={patients.length >= 2 ? 'xs' : 'sm'}
                                                        name={eventPatient.patient?.fullName ?? ''}
                                                    />
                                                ))}
                                                <Tooltip
                                                    rootProps={{
                                                        open: patients.length < 2 ? false : undefined
                                                    }}
                                                    variant="light"
                                                    triggerProps={{
                                                        style: {
                                                            width: '100%',
                                                            overflow: 'hidden'
                                                        }
                                                    }}
                                                    content={patients.map(eventPatient => (
                                                        <span key={eventPatient.patientId}>
                                                            {eventPatient.patient?.fullName ?? '-'}
                                                        </span>
                                                    ))}
                                                >
                                                    <TableBodyCellText variant="dark">
                                                        {patients.length >= 2
                                                            ? t('patients:x_patients', {count: patients.length})
                                                            : patients[0].patient?.fullName ?? '-'}
                                                    </TableBodyCellText>
                                                </Tooltip>
                                            </Flexbox>
                                        )}
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.patientId]),
                            id: UserEventsColumn.enum.patientId,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.sector ? t(projectSectorToLabel[dataItem.sector]) : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.sector]),
                            id: UserEventsColumn.enum.sector,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.isRemote ? t('commons:yes') : t('commons:no')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UserEventsColumn.enum.isRemote]),
                            id: UserEventsColumn.enum.isRemote,
                            size: () => 140
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const project = getEventProject(dataItem)
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {permissions?.projects?.read && project ? (
                                                <Link
                                                    to={generatePath(routes.PROJECT_DETAILS_ROUTE.handle.path, {
                                                        id: project.id
                                                    })}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    {project.code}
                                                </Link>
                                            ) : (
                                                project?.code ?? '-'
                                            )}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('commons:pri_n'),
                            id: UserEventsColumn.enum.projectCode,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const project = getEventProject(dataItem)
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {project?.startDate ? formatLocaleDate(project.startDate, 'll') : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:pri_start_date'),
                            id: UserEventsColumn.enum.projectStartDate,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const project = getEventProject(dataItem)
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {project?.expirationDate
                                                ? formatLocaleDate(project.expirationDate, 'll')
                                                : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:pri_expiration_date'),
                            id: UserEventsColumn.enum.projectExpirationDate,
                            size: () => 120,
                            minSize: () => 80
                        }
                    ]}
                />
            )}
        </StyledUserDetailsEventsWrapper>
    )
}
